// =========================
// PROFILE VIEW
// =========================
import App from './../../App';
import { html, render } from 'lit-html';
import { gotoRoute } from './../../Router';
import Auth from './../../Auth';
import Utils from './../../Utils';

// This is the ProfileView class that handles the profile page
// It includes methods for rendering the page and handling user interactions
class ProfileView {
  constructor() {
    this.user = null;
    this.supplier = null;
    this.loadedPosts = [];
    this.batchSize = 5;
    this.currentIndex = 0;
    this.observer = null;
    this.loadingPosts = false;
  }

  // This method initializes the view
  // It fetches the user data based on the URL parameters or current user
  async init() {
    const urlParams = new URLSearchParams(window.location.search);
    const userId = urlParams.get('user') || Auth.currentUser.id;

    try {
      const res = await fetch(`${App.apiBase}/user/${userId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });

      if (!res.ok) throw new Error('Failed to fetch user data');
      this.user = await res.json();

      if (this.user.role === 'supplier') {
        this.supplier = await Utils.getSupplierDetails(this.user._id);
        if (this.supplier?.businessName) {
          this.user.businessName = this.supplier.businessName;
        }
      }

      this.loadedPosts = [];
      this.currentIndex = 0;
      this.loadMorePosts();
    } catch (err) {
      console.error('Error fetching user/supplier data:', err);
      this.user = Auth.currentUser;
    }

    document.title = `PopIn – ${Utils.getDisplayName(this.user)}`;

    this.render();
    Utils.pageIntroAnim();
  }

  // This method loads more posts for the user
  // It fetches the next batch of posts and enriches them with additional data
  async loadMorePosts() {
    this.loadingPosts = true;
    this.render();

    const allPosts = (this.user?.posts || []).sort(
      (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
    );
    const nextBatch = allPosts.slice(
      this.currentIndex,
      this.currentIndex + this.batchSize
    );

    const enrichedPosts = await Promise.all(
      nextBatch.map((post) => {
        return document.createElement('popin-post').enrichPost(post);
      })
    );

    this.loadedPosts = [...this.loadedPosts, ...enrichedPosts];
    this.currentIndex += this.batchSize;

    this.loadingPosts = false;
    this.render();
  }

  // This method sets up an Intersection Observer to load more posts when the last post is visible
  setupObserver() {
    if (this.observer) this.observer.disconnect();

    const posts = document.querySelectorAll(
      'sl-tab-panel[name="posts"] popin-post'
    );
    const lastPost = posts[posts.length - 1];

    if (!lastPost) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          const totalPosts = this.user?.posts?.length || 0;
          if (this.loadedPosts.length < totalPosts) {
            this.loadMorePosts();
          }
        }
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 1.0,
      }
    );

    this.observer.observe(lastPost);
  }

  render() {
    const user = this.user || {};
    const displayName = Utils.getDisplayName(user);
    const profileImage = Utils.getProfileImage(user);

    const latestTrophies = (user.trophies || [])
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, 3);

    const template = html`
      <div class="layout">
        <popin-side-nav></popin-side-nav>
        <div class="main-area">
          <popin-top-bar .user=${Auth.currentUser}></popin-top-bar>
          <div class="main-content">
            <div class="profile-header">
              <img
                src="${profileImage}"
                alt="Profile Image"
                class="profile-image"
              />
              <div class="profile-info">
                <h1>${displayName}</h1>
                <h3 class="location">${user.location || ''}</h3>
                <hr />
                <p class="bio">${user.bio || ''}</p>
                ${user._id === Auth.currentUser.id
                  ? html`
                      <sl-button
                        variant="neutral"
                        outline
                        class="anim-in"
                        @click=${() => gotoRoute('/editProfile')}
                      >
                        Edit Profile
                      </sl-button>
                    `
                  : ''}
              </div>
            </div>

            <div class="profile-trophies">
              <div class="trophies-list">
                ${latestTrophies.length > 0
                  ? latestTrophies.map(
                      (trophy) => html`
                        <div class="trophy-card">
                          <img src="images/trophy.png" />
                          <div>
                            <h4>${trophy.name}</h4>
                            <span
                              >${new Date(
                                trophy.date
                              ).toLocaleDateString()}</span
                            >
                          </div>
                        </div>
                      `
                    )
                  : html`<p>No trophies earned yet.</p>`}
              </div>
            </div>

            <div class="profile-tabs">
              <sl-tab-group placement="top">
                <sl-tab slot="nav" panel="posts">POSTS</sl-tab>

                ${user.role === 'supplier'
                  ? html`
                      <sl-tab slot="nav" panel="services">SERVICES</sl-tab>
                      <sl-tab slot="nav" panel="profile">PROFILE</sl-tab>
                      <sl-tab slot="nav" panel="contact">CONTACT</sl-tab>
                    `
                  : html`
                      <sl-tab slot="nav" panel="events">EVENTS</sl-tab>
                      <sl-tab slot="nav" panel="profile">PROFILE</sl-tab>
                    `}

                <sl-tab-panel name="posts">
                  ${this.user._id === Auth.currentUser.id
                    ? html`<popin-poster .user=${this.user}></popin-poster>`
                    : null}
                  ${this.loadingPosts && this.loadedPosts.length === 0
                    ? html`<sl-spinner
                        style="margin: 2rem auto; display: block;"
                      ></sl-spinner>`
                    : this.loadedPosts.length > 0
                      ? this.loadedPosts.map(
                          (post) =>
                            html`<popin-post .post=${post}></popin-post>`
                        )
                      : html`<p>No posts yet.</p>`}
                </sl-tab-panel>

                ${user.role === 'supplier'
                  ? html`
                      <sl-tab-panel name="services">
                        ${this.supplier?.services?.length
                          ? html`
                              <div class="services-grid">
                                ${this.supplier.services.map(
                                  (service) => html`
                                    <div class="service-card">
                                      <sl-icon
                                        name="briefcase-fill"
                                        class="service-icon"
                                      ></sl-icon>
                                      <h3>${service}</h3>
                                    </div>
                                  `
                                )}
                              </div>
                            `
                          : html`<p>
                              This supplier has not listed any services yet.
                            </p>`}
                      </sl-tab-panel>

                      <sl-tab-panel name="profile">
                        <div class="profile-details">
                          <p>
                            <strong>Email:</strong>
                            <a href="mailto:${user.email}">${user.email}</a>
                          </p>
                          ${this.supplier
                            ? html`
                                <p>
                                  <strong>Contact Email:</strong>
                                  <a href="mailto:${this.supplier.contactEmail}"
                                    >${this.supplier.contactEmail}</a
                                  >
                                </p>
                                <p>
                                  <strong>Rating:</strong>
                                  ${this.supplier.reviewCount > 0
                                    ? `${this.supplier.rating.toFixed(1)} ★ (${this.supplier.reviewCount})`
                                    : 'Not rated'}
                                </p>
                                ${(this.supplier?.reviews?.length || 0) > 0
                                  ? html`
                                      <div class="reviews-section">
                                        <h4>Reviews:</h4>
                                        ${this.supplier.reviews.map(
                                          (r) => html`
                                            <div class="review">
                                              <p>
                                                <strong>Rating:</strong>
                                                ${r.rating} ★
                                              </p>
                                              <p>
                                                <strong>Comment:</strong>
                                                ${r.comment}
                                              </p>
                                              <p>
                                                <small
                                                  >${new Date(
                                                    r.date
                                                  ).toLocaleDateString()}</small
                                                >
                                              </p>
                                            </div>
                                          `
                                        )}
                                      </div>
                                    `
                                  : ''}
                                ${this.supplier.website
                                  ? html`
                                      <p>
                                        <strong>Website:</strong>
                                        <a
                                          href="${this.supplier.website}"
                                          target="_blank"
                                          >${this.supplier.website}</a
                                        >
                                      </p>
                                    `
                                  : ''}
                                ${(this.supplier?.socialLinks?.length || 0) > 0
                                  ? html`
                                      <div class="social-links">
                                        <h4>Social Links:</h4>
                                        <ul>
                                          ${this.supplier.socialLinks.map(
                                            (link) => html`
                                              <li>
                                                <strong>${link.type}:</strong>
                                                <a
                                                  href="${link.link}"
                                                  target="_blank"
                                                  >${link.link}</a
                                                >
                                              </li>
                                            `
                                          )}
                                        </ul>
                                      </div>
                                    `
                                  : ''}
                              `
                            : ''}
                        </div>
                      </sl-tab-panel>

                      <sl-tab-panel name="contact">
                        <p>
                          Contact:
                          <a
                            href="mailto:${this.supplier?.contactEmail ||
                            user.email}"
                          >
                            ${this.supplier?.contactEmail || user.email}
                          </a>
                        </p>
                      </sl-tab-panel>
                    `
                  : html`
                      <sl-tab-panel name="events">
                        <p>Coming soon: User’s attended or hosted events.</p>
                      </sl-tab-panel>

                      <sl-tab-panel name="profile">
                        <div class="profile-details">
                          <p>
                            <strong>Email:</strong>
                            <a href="mailto:${user.email}">${user.email}</a>
                          </p>
                        </div>
                      </sl-tab-panel>
                    `}
              </sl-tab-group>
            </div>
          </div>
        </div>
      </div>
    `;

    render(template, App.rootEl);
    this.setupObserver();
  }
  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }
}

export default new ProfileView();
